import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { DomSanitizer } from '@angular/platform-browser';
import { TdoFileModel } from '../models/ui/tdo-file.model';
import { DocumentDataModel } from '../models/document/document-data.model';

@Injectable({
  providedIn: 'root'
})
export class FileHelperService {
  static MIME_JPG = 'image/jpeg';
  static MIME_PNG = 'image/png';

  static MIME_AVI = 'video/avi';
  static MIME_MOV = 'video/quicktime';
  static MIME_MP4 = 'video/mp4';

  static MIME_MP3 = 'audio/mpeg';
  static MIME_WAV = 'audio/wav';
  static MIME_POWERPOINT_PPTX = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
  static MIME_EXCEL_XLXS = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  static MIME_APPLICATION_XML = 'application/xml';
  static MIME_TEXT_XML = 'text/xml';
  static MIME_JSON = 'application/json';

  static MIME_PDF = 'application/pdf';

  private thumbsize = 400;
  private width = 0;
  private height = 0;

  constructor(private sanitizer: DomSanitizer) { }

  getDataAndThumbnailFromFile(file: File): Observable<TdoFileModel> {
    return new Observable<TdoFileModel>((observer) => {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      const model = new TdoFileModel();
      try {
        model.originalExtension = file.name.split('.')[1];
      } catch (e: any) {
        model.originalExtension = 'jpg';
        model.thumbnailExtension = 'png';
      }

      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = (event) => {
        model.fileDataBase64 = reader.result as string;
        model.fileDataBlob = this.base64ToBlob(model.fileDataBase64, file.type);
        model.fileMimeType = file.type;

        switch (file.type) {
          case FileHelperService.MIME_JPG:
          case FileHelperService.MIME_PNG:
            model.thumbnailExtension = 'png';
            const img = new Image();
            img.src = model.fileDataBase64;
            img.onload = (data) => {

      canvas.width = img.width;
      canvas.height = img.height;

      // Clear the canvas with transparent background
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.fillStyle = 'rgba(0,0,0,0)';
      context.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw the image preserving transparency
      context.drawImage(img, 0, 0, img.width, img.height);

              const ret = this.getImageThumbnailFromCanvas(canvas);

              model.thumbnailDataBase64 = ret.thumbnailDataBase64;
              model.thumbnailDataBlob = ret.thumbnailDataBlob;
              model.thumbMimeType = ret.fileMimeType;
              observer.next(model);
            };
            break;
          case FileHelperService.MIME_AVI:
          case FileHelperService.MIME_MOV:
          case FileHelperService.MIME_MP4:
            const video = document.createElement('video');
            canvas.addEventListener('error', () => {
              console.log('Canvas error gathering video thumbnail data');
            });

            video.addEventListener('error', () => {
              console.log('Video error gathering video thumbnail data');
            });

            let startX = 0;
            let startY = 0;
            let drawX = 0;
            let drawY = 0;
            const playImageSize = 48;

            video.addEventListener('canplay', (canvasEvent) => {
              canvas.width = this.thumbsize;
              canvas.height = this.thumbsize;

              if (video.videoWidth > this.thumbsize) {
                startX = (video.videoWidth - this.thumbsize) * 0.5;
              } else {
                drawX = (this.thumbsize - video.videoWidth) * 0.5;
              }

              if (video.videoHeight > this.thumbsize) {
                startY = (video.videoHeight - this.thumbsize) * 0.5;
              } else {
                drawY = (this.thumbsize - video.videoHeight) * 0.5;
              }

              context.fillStyle = 'black';
              context.fillRect(0, 0, this.thumbsize, this.thumbsize);
              context.drawImage(video, startX, startY, this.thumbsize, this.thumbsize, drawX, drawY, this.thumbsize, this.thumbsize);

              const playImage = new Image();
              playImage.onload = (imageEvent) => {
                context.drawImage(playImage,
                  0, 0,
                  playImage.width, playImage.height,
                  this.thumbsize - playImageSize, this.thumbsize - playImageSize,
                  playImageSize, playImageSize
                );
                model.thumbnailDataBase64 = canvas.toDataURL(FileHelperService.MIME_JPG);
                model.thumbnailDataBlob = this.base64ToBlob(model.thumbnailDataBase64, FileHelperService.MIME_JPG);
                observer.next(model);
              };
              playImage.src = './assets/Images/image-organizer/thumbnail/play_circle_white_24dp.svg';
            });

            if (file.type) {
              video.setAttribute('type', file.type);
            }

            video.preload = 'auto';
            video.src = window.URL.createObjectURL(file);
            video.load();
            break;
          case FileHelperService.MIME_MP3:
          case FileHelperService.MIME_WAV:
          case FileHelperService.MIME_PDF:
          case FileHelperService.MIME_POWERPOINT_PPTX:
          case FileHelperService.MIME_EXCEL_XLXS:
          case FileHelperService.MIME_APPLICATION_XML:
          case FileHelperService.MIME_TEXT_XML:
          case FileHelperService.MIME_JSON:
            const audioImageSize = 128;
            canvas.width = this.thumbsize;
            canvas.height = this.thumbsize;
            context.fillStyle = 'black';
            context.fillRect(0, 0, this.thumbsize, this.thumbsize);
            const staticImage = new Image();
            staticImage.onload = (audioEvent) => {
              context.drawImage(staticImage,
                0, 0,
                staticImage.width, staticImage.height,
                this.thumbsize * 0.5 - audioImageSize * 0.5, this.thumbsize * 0.5 - audioImageSize * 0.5,
                audioImageSize, audioImageSize
              );
              model.thumbnailDataBase64 = canvas.toDataURL(FileHelperService.MIME_JPG);
              model.thumbnailDataBlob = this.base64ToBlob(model.thumbnailDataBase64, FileHelperService.MIME_JPG);
              observer.next(model);
            };
            switch (file.type) {
              case FileHelperService.MIME_MP3:
              case FileHelperService.MIME_WAV:
                staticImage.src = './assets/Images/image-organizer/thumbnail/audiotrack_white_24dp.svg';
                break;
              case FileHelperService.MIME_PDF:
                staticImage.src = './assets/Images/image-organizer/thumbnail/picture_as_pdf_white_24dp.svg';
                break;
              case FileHelperService.MIME_POWERPOINT_PPTX:
                staticImage.src = './assets/Images/image-organizer/thumbnail/powerpoint_white.svg';
                break;
              case FileHelperService.MIME_EXCEL_XLXS:
                staticImage.src = './assets/Images/image-organizer/thumbnail/excel_white.svg';
                break;
              case FileHelperService.MIME_JSON:
                staticImage.src = './assets/Images/image-organizer/thumbnail/json_white.svg';
                break;
              case FileHelperService.MIME_APPLICATION_XML:
              case FileHelperService.MIME_TEXT_XML:
                staticImage.src = './assets/Images/image-organizer/thumbnail/xml_white.svg';
                break;
            }
            break;
        }
      };
    });
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////


  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  getImageThumbnailFromCanvas(canvas: HTMLCanvasElement) {
    const model = new TdoFileModel();

    const newCanvas = document.createElement('canvas');
    const context = newCanvas.getContext('2d');

    const imageRatio = canvas.width / canvas.height;
    newCanvas.height = Math.min(this.thumbsize, this.thumbsize / imageRatio);
    newCanvas.width = Math.min(this.thumbsize, this.thumbsize * imageRatio);
    
    // Clear the canvas with transparent background
    context.clearRect(0, 0, newCanvas.width, newCanvas.height);
    context.fillStyle = 'rgba(0,0,0,0)';
    context.fillRect(0, 0, newCanvas.width, newCanvas.height);
    
    // Draw the image preserving transparency
    context.drawImage(canvas, 0, 0, canvas.width, canvas.height, 0, 0, newCanvas.width, newCanvas.height);

    // Use PNG format to preserve transparency
    model.thumbnailDataBase64 = newCanvas.toDataURL(FileHelperService.MIME_PNG);
    model.thumbnailDataBlob = this.base64ToBlob(model.thumbnailDataBase64, FileHelperService.MIME_PNG);
    model.fileMimeType = FileHelperService.MIME_PNG;

    return model;
  }

  // Utilities
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  base64ToBlob(base64: string, mime: string): Blob {
    base64 = base64.replace(`data:${mime};base64,`, '');
    mime = mime || '';
    const sliceSize = 1024;
    const byteChars = atob(base64);
    const byteArrays = [];

    for (let offset = 0, len = byteChars.length; offset < len; offset += sliceSize) {
      const slice = byteChars.slice(offset, offset + sliceSize);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);

      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: mime });
  }
}
